import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

// ─── helpers ────────────────────────────────────────────────────────────────

const MESES = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril',
    'Maio', 'Junho', 'Julho', 'Agosto',
    'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const diasNoMes = (year, month) => new Date(year, month + 1, 0).getDate();
const primeiroDiaSemana = (year, month) => new Date(year, month, 1).getDay();

const formatKey = (year, month, day) =>
    `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

// Converte "HH:MM" em minutos totais para comparação
const toMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
};

// ─── classificadores de cor ──────────────────────────────────────────────────

// Abertura: ≤10:30 verde | 10:31–11:00 amarelo | >11:00 vermelho
const corAbertura = (timeStr) => {
    const min = toMinutes(timeStr);
    if (min === null) return null;
    if (min <= 10 * 60 + 30) return { bg: '#e8f5e9', color: '#2e7d32' }; // verde
    if (min <= 11 * 60)       return { bg: '#fff8e1', color: '#f57f17' }; // amarelo
    return                           { bg: '#fce4ec', color: '#c62828' }; // vermelho
};

// Fechamento: <22:45 vermelho | 22:45–23:00 amarelo | ≥23:01 verde
const corFechamento = (timeStr) => {
    const min = toMinutes(timeStr);
    if (min === null) return null;
    if (min < 22 * 60 + 45)  return { bg: '#fce4ec', color: '#c62828' }; // vermelho
    if (min <= 23 * 60)       return { bg: '#fff8e1', color: '#f57f17' }; // amarelo
    return                           { bg: '#e8f5e9', color: '#2e7d32' }; // verde
};

// ─── sub-componente: mini calendário de 1 mês ───────────────────────────────

function MiniCalendario({ year, month, aberturas }) {
    const total = diasNoMes(year, month);
    const offset = primeiroDiaSemana(year, month);
    const hoje = new Date();

    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(d);

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: 2,
                p: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                minWidth: 0,
            }}
        >
            {/* Nome do mês */}
            <Typography
                variant="subtitle2"
                fontWeight="bold"
                align="center"
                sx={{ mb: 1, color: '#333', letterSpacing: 0.5 }}
            >
                {MESES[month]}
            </Typography>

            {/* Cabeçalho dias da semana */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                {DIAS_SEMANA.map((d) => (
                    <Typography
                        key={d}
                        variant="caption"
                        align="center"
                        sx={{ color: '#999', fontSize: '0.6rem', fontWeight: 600 }}
                    >
                        {d}
                    </Typography>
                ))}
            </Box>

            {/* Grade de dias */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {cells.map((day, idx) => {
                    if (!day) return <Box key={`empty-${idx}`} />;

                    const key = formatKey(year, month, day);
                    const entrada = aberturas[key];
                    const hora = entrada?.abertura;
                    const fechamento = entrada?.fechamento;

                    const coresAb = corAbertura(hora);
                    const coresFech = corFechamento(fechamento);

                    const isHoje =
                        hoje.getFullYear() === year &&
                        hoje.getMonth() === month &&
                        hoje.getDate() === day;

                    const isFuturo = new Date(year, month, day) > hoje;

                    return (
                        <Box
                            key={key}
                            title={
                                hora
                                    ? `${day}/${month + 1} — Abertura: ${hora} | Fechamento: ${fechamento}`
                                    : `${day}/${month + 1}: sem movimento`
                            }
                            sx={{
                                borderRadius: '4px',
                                p: '2px 1px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px',
                                bgcolor: isFuturo ? 'transparent' : '#f9f9f9',
                                border: isHoje ? '2px solid #1976d2' : '2px solid transparent',
                                transition: 'transform 0.1s',
                                '&:hover': { transform: hora ? 'scale(1.1)' : 'none', zIndex: 1 },
                            }}
                        >
                            {/* Número do dia */}
                            <Typography
                                sx={{
                                    fontSize: '0.6rem',
                                    fontWeight: isHoje ? 800 : 500,
                                    color: isFuturo ? '#ccc' : '#555',
                                    lineHeight: 1.2,
                                }}
                            >
                                {day}
                            </Typography>

                            {!isFuturo && hora && (
                                <>
                                    {/* Caixa de abertura */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            borderRadius: '2px',
                                            bgcolor: coresAb?.bg || '#f5f5f5',
                                            px: '1px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.48rem',
                                                color: coresAb?.color || '#999',
                                                fontWeight: 700,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {hora}
                                        </Typography>
                                    </Box>

                                    {/* Caixa de fechamento */}
                                    <Box
                                        sx={{
                                            width: '100%',
                                            borderRadius: '2px',
                                            bgcolor: coresFech?.bg || '#f5f5f5',
                                            px: '1px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.48rem',
                                                color: coresFech?.color || '#999',
                                                fontWeight: 700,
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {fechamento}
                                        </Typography>
                                    </Box>
                                </>
                            )}

                            {/* Sem movimento */}
                            {!isFuturo && !hora && (
                                <Box sx={{ width: '100%', borderRadius: '2px', bgcolor: '#f0f0f0', px: '1px', textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '0.4rem', color: '#bbb', lineHeight: 1.3 }}>
                                        —
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ─── componente principal ────────────────────────────────────────────────────

function Abertura() {
    const { authenticatedFetch, logout } = useAuth();

    const [year, setYear] = useState(new Date().getFullYear());
    const [aberturas, setAberturas] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAberturas = useCallback(async (y) => {
        setLoading(true);
        setError(null);
        try {
            const response = await authenticatedFetch(
                `${API_BASE_URL}/api/abertura-loja/?year=${y}`
            );
            if (!response.ok) {
                if (response.status === 401) logout();
                throw new Error(`Erro ${response.status}`);
            }
            const data = await response.json();
            setAberturas(data.aberturas || {});
        } catch (err) {
            setError(`Não foi possível carregar os dados: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch, logout]);

    useEffect(() => {
        fetchAberturas(year);
    }, [year, fetchAberturas]);

    // ─── Estatísticas ────────────────────────────────────────────────────────
    const valores = Object.values(aberturas).filter(e => e?.abertura);
    const totalDias = valores.length;

    const calcMedia = (campo) => {
        if (totalDias === 0) return null;
        const soma = valores.reduce((acc, entrada) => {
            if (!entrada?.[campo]) return acc;
            const [hh, mm] = entrada[campo].split(':').map(Number);
            return acc + hh * 60 + mm;
        }, 0);
        const media = soma / totalDias;
        return `${String(Math.floor(media / 60)).padStart(2, '0')}:${String(Math.round(media % 60)).padStart(2, '0')}`;
    };

    const mediaAbertura = calcMedia('abertura');
    const mediaFechamento = calcMedia('fechamento');

    return (
        <Box sx={{ maxWidth: 900, p: 3, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#f8f9fa', minHeight: '100vh' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="#222">
                        Abertura da Loja
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Horário do primeiro e último item registrado por dia
                    </Typography>
                </Box>

                {/* Seletor de ano */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', borderRadius: 2, px: 2, py: 1, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                    <IconButton size="small" onClick={() => setYear((y) => y - 1)} disabled={loading}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 50, textAlign: 'center' }}>
                        {year}
                    </Typography>
                    <IconButton size="small" onClick={() => setYear((y) => y + 1)} disabled={loading || year >= new Date().getFullYear()}>
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Legendas + Médias */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

                {/* Abertura */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 70 }}>
                        Abertura:
                    </Typography>
                    <Chip size="small" label="≤ 10:30" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.7rem' }} />
                    <Chip size="small" label="10:31 – 11:00" sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 700, fontSize: '0.7rem' }} />
                    <Chip size="small" label="> 11:00" sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: '0.7rem' }} />
                    {mediaAbertura && (
                        <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important' }} />}
                            label={`Média: ${mediaAbertura}`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                    )}
                </Box>

                {/* Fechamento */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ minWidth: 70 }}>
                        Fechamento:
                    </Typography>
                    <Chip size="small" label="< 22:45" sx={{ bgcolor: '#fce4ec', color: '#c62828', fontWeight: 700, fontSize: '0.7rem' }} />
                    <Chip size="small" label="22:45 – 23:00" sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontWeight: 700, fontSize: '0.7rem' }} />
                    <Chip size="small" label="≥ 23:01" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.7rem' }} />
                    {mediaFechamento && (
                        <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: '0.9rem !important' }} />}
                            label={`Média: ${mediaFechamento}`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                    )}
                </Box>

            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {/* Grid de calendários */}
            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 4 }}>
                    <CircularProgress size={24} />
                    <Typography>Carregando calendário...</Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 2,
                    }}
                >
                    {Array.from({ length: 12 }, (_, i) => (
                        <MiniCalendario
                            key={i}
                            year={year}
                            month={i}
                            aberturas={aberturas}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default Abertura;